import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { debounce } from "../utilities/helpers"
import {
    fetchCommunityPlaces,
    fetchCommunityPlacesFresh,
    mergeEnrichedChaperee,
} from "../utilities/communities"
import { isCommunityRefreshDue } from "../utilities/communityPoll"
import { LOCALSTORAGE_COMMUNITY_SIDEBAR_OPEN } from "../const/storage"
import {
    COMMUNITY_POLL_INTERVAL_MS,
    COMMUNITY_POLL_CHECK_MS,
    COMMUNITY_POLL_IDLE_LIMIT_MS,
} from "../const/numbers"
import {
    createCommunity,
    deleteCommunity,
    addChaperee,
    updateChapereeOrder,
    deleteChaperees,
    updateChaperee,
    insertChaperee,
    getCommunities,
    getEnrichedChaperee,
    patchCommunity,
} from "../services/chaperonConnector"
import { sendInvitation } from "../services/chaperonInvitationsConnector"
import { lookupUserByIdentifier } from "../services/userService"

// Community list state, background refresh and member/community actions
// behind the Members sidebar. Shared by /community and its sub-pages;
// render it with <CommunitySidebar manager={...} />.
export function useCommunityManager() {
    const navigate = useNavigate()
    const [communities, setCommunities] = useState([])
    const [isLoadingCommunities, setIsLoadingCommunities] = useState(true)
    // Remembered across pages and visits; open unless the user closed it.
    const [isSidebarOpen, setIsSidebarOpen] = useState(
        () => localStorage.getItem(LOCALSTORAGE_COMMUNITY_SIDEBAR_OPEN) !== "false",
    )
    const [openAddMemberModal, setOpenAddMemberModal] = useState(false)
    const [openEditMemberModal, setOpenEditMemberModal] = useState(false)
    const [editingMember, setEditingMember] = useState(null)
    const [openCommunitySettingsModal, setOpenCommunitySettingsModal] =
        useState(false)
    const [editingCommunity, setEditingCommunity] = useState(null)
    const [toasts, setToasts] = useState([])

    useEffect(() => {
        localStorage.setItem(
            LOCALSTORAGE_COMMUNITY_SIDEBAR_OPEN,
            String(isSidebarOpen),
        )
    }, [isSidebarOpen])

    // Effect for reading community members from localStorage
    useEffect(() => {
        // When the list was last (re)loaded, by any trigger. The background
        // poll below is measured from this, so a manual refresh or an action
        // in this tab restarts the 10 minute wait.
        let lastLoadedAt = Date.now()
        let quietRefreshInFlight = false
        // Last mouse/keyboard/touch input. The background poll stops once this
        // is older than COMMUNITY_POLL_IDLE_LIMIT_MS (a tab left open and
        // visible would otherwise poll all day) and resumes on the next input.
        let lastActivityAt = Date.now()

        const loadCommunities = async () => {
            setIsLoadingCommunities(true)
            const userData = localStorage.getItem("chaperon_user_data")
            if (userData) {
                const user = JSON.parse(userData)
                if (user.id) {
                    // A cached copy older than one poll interval is
                    // refetched on load instead of served as-is.
                    const communitiesWithPlaces =
                        await fetchCommunityPlacesFresh(
                            user.id,
                            COMMUNITY_POLL_INTERVAL_MS,
                        )
                    setCommunities(communitiesWithPlaces)
                    lastLoadedAt = Date.now()
                }
            }
            setIsLoadingCommunities(false)
        }

        // Background refresh: no loading state (the list stays put), and a
        // failed fetch keeps what is displayed. fetchCommunityPlaces returns
        // [] both for "no communities" and for an API failure, but only a
        // successful API fetch re-populates the cache key, so an empty cache
        // afterwards means the fetch failed.
        const refreshCommunitiesQuietly = async () => {
            const userData = localStorage.getItem("chaperon_user_data")
            const user = userData ? JSON.parse(userData) : null
            if (!user?.id) return

            quietRefreshInFlight = true
            const previousCache = localStorage.getItem(
                "chaperon_user_communities",
            )
            try {
                localStorage.removeItem("chaperon_user_communities")
                const fresh = await fetchCommunityPlaces(user.id)
                if (localStorage.getItem("chaperon_user_communities") === null) {
                    if (previousCache) {
                        localStorage.setItem(
                            "chaperon_user_communities",
                            previousCache,
                        )
                    }
                    return
                }
                setCommunities(fresh)
                lastLoadedAt = Date.now()
            } finally {
                quietRefreshInFlight = false
            }
        }

        const refreshIfDue = () => {
            if (
                isCommunityRefreshDue({
                    now: Date.now(),
                    lastLoadedAt,
                    lastActivityAt,
                    visible: document.visibilityState === "visible",
                    inFlight: quietRefreshInFlight,
                    intervalMs: COMMUNITY_POLL_INTERVAL_MS,
                    idleLimitMs: COMMUNITY_POLL_IDLE_LIMIT_MS,
                })
            ) {
                refreshCommunitiesQuietly()
            }
        }
        const pollId = setInterval(refreshIfDue, COMMUNITY_POLL_CHECK_MS)
        // A tab that was hidden catches up as soon as it is seen again,
        // instead of waiting for the next check.
        document.addEventListener("visibilitychange", refreshIfDue)

        // Input resets the idle clock; the first input after an idle stretch
        // also catches the list up straight away. Cheap by design: one
        // timestamp write, at most once per second.
        const activityEvents = [
            "mousemove",
            "mousedown",
            "keydown",
            "wheel",
            "touchstart",
        ]
        const onActivity = () => {
            const now = Date.now()
            if (now - lastActivityAt < 1000) return
            const wasIdle = now - lastActivityAt > COMMUNITY_POLL_IDLE_LIMIT_MS
            lastActivityAt = now
            if (wasIdle) refreshIfDue()
        }
        activityEvents.forEach((name) =>
            window.addEventListener(name, onActivity, { passive: true }),
        )

        // Security Fix #3: Debounce handler to prevent rate-limit abuse
        // Targeted refresh for a single community (Performance Fix #5)
        const debouncedRefreshCommunity = debounce(async (communityId) => {
            try {
                const userData = localStorage.getItem("chaperon_user_data")
                if (!userData) return

                const user = JSON.parse(userData)

                // Fetch updated community data from API
                const response = await getCommunities(user.id)
                if (!response || !response.communities) return

                const updatedCommunity = response.communities.find(
                    (c) => c.id === communityId,
                )
                if (!updatedCommunity) {
                    // User no longer has access to this community (e.g., removed member).
                    setCommunities((prevCommunities) =>
                        prevCommunities.filter((c) => c.id !== communityId),
                    )

                    // Keep local cache in sync by removing the missing community.
                    const cachedData = localStorage.getItem(
                        "chaperon_user_communities",
                    )
                    if (cachedData) {
                        try {
                            const parsed = JSON.parse(cachedData)
                            if (
                                parsed.communities &&
                                Array.isArray(parsed.communities)
                            ) {
                                parsed.communities = parsed.communities.filter(
                                    (c) => c.id !== communityId,
                                )
                                localStorage.setItem(
                                    "chaperon_user_communities",
                                    JSON.stringify(parsed),
                                )
                            }
                        } catch (cacheErr) {
                            console.error(
                                "Failed to update localStorage cache:",
                                cacheErr,
                            )
                        }
                    }
                    return
                }

                // Enrich chaperee data for this community
                if (updatedCommunity.chaperees) {
                    let chapereeReferences = []

                    if (typeof updatedCommunity.chaperees === "string") {
                        try {
                            chapereeReferences = JSON.parse(
                                updatedCommunity.chaperees,
                            )
                        } catch (parseError) {
                            console.error(
                                "Failed to parse chaperees JSON for community refresh:",
                                parseError,
                            )
                            chapereeReferences = []
                        }
                    } else {
                        chapereeReferences = updatedCommunity.chaperees
                    }
                    if (Array.isArray(chapereeReferences)) {
                        const enrichedChaperees = []

                        for (const reference of chapereeReferences) {
                            try {
                                const enrichedData = await getEnrichedChaperee(
                                    reference.id,
                                )
                                if (enrichedData) {
                                    enrichedChaperees.push(
                                        mergeEnrichedChaperee(
                                            reference,
                                            enrichedData,
                                        ),
                                    )
                                }
                            } catch (err) {
                                console.error(
                                    `Failed to enrich chaperee ${reference.id}:`,
                                    err,
                                )
                            }
                        }

                        updatedCommunity.chaperees = enrichedChaperees
                    }
                }

                // Note: The UI expects separate member arrays (`community.places` and
                // `community.persons`) as generated by fetchCommunityPlaces.
                if (
                    updatedCommunity.chaperees &&
                    Array.isArray(updatedCommunity.chaperees)
                ) {
                    // Keep ordering consistent with fetchCommunityPlaces:
                    // pinned-first, then by `order` ascending.
                    const sortedChaperees = [
                        ...updatedCommunity.chaperees,
                    ].sort((a, b) => {
                        const aPinned = a?.pinned ? 1 : 0
                        const bPinned = b?.pinned ? 1 : 0

                        if (aPinned !== bPinned) {
                            // Pinned items first
                            return bPinned - aPinned
                        }

                        const aOrder =
                            a?.order !== undefined && a?.order !== null
                                ? a.order
                                : Number.MAX_SAFE_INTEGER
                        const bOrder =
                            b?.order !== undefined && b?.order !== null
                                ? b.order
                                : Number.MAX_SAFE_INTEGER

                        if (aOrder === bOrder) {
                            return 0
                        }

                        return aOrder - bOrder
                    })

                    updatedCommunity.places = sortedChaperees.filter(
                        (c) => c.type === "place",
                    )
                    updatedCommunity.persons = sortedChaperees.filter(
                        (c) => c.type === "person",
                    )
                } else {
                    updatedCommunity.places = []
                    updatedCommunity.persons = []
                }

                // Update just this community in the state
                setCommunities((prevCommunities) => {
                    const existingIndex = prevCommunities.findIndex(
                        (c) => c.id === communityId,
                    )

                    // Community exists: replace it.
                    if (existingIndex !== -1) {
                        return prevCommunities.map((c) =>
                            c.id === communityId ? updatedCommunity : c,
                        )
                    }

                    // Community is new for this user (e.g., newly joined): add it.
                    return [updatedCommunity, ...prevCommunities]
                })

                // Security Fix #6: Also update localStorage cache with enriched data
                const cachedData = localStorage.getItem(
                    "chaperon_user_communities",
                )
                if (cachedData) {
                    try {
                        const parsed = JSON.parse(cachedData)
                        if (
                            parsed.communities &&
                            Array.isArray(parsed.communities)
                        ) {
                            // Make sure we strip out the temporary member arrays (places/persons)
                            // before saving back to the cache to match API format.
                            const { places, persons, ...communityToCache } =
                                updatedCommunity
                            const serializedCommunity = {
                                ...communityToCache,
                                chaperees: JSON.stringify(
                                    communityToCache.chaperees || [],
                                ),
                            }

                            const existingIndex = parsed.communities.findIndex(
                                (c) => c.id === communityId,
                            )

                            if (existingIndex !== -1) {
                                parsed.communities = parsed.communities.map(
                                    (c) =>
                                        c.id === communityId
                                            ? serializedCommunity
                                            : c,
                                )
                            } else {
                                parsed.communities = [
                                    serializedCommunity,
                                    ...parsed.communities,
                                ]
                            }
                            localStorage.setItem(
                                "chaperon_user_communities",
                                JSON.stringify(parsed),
                            )
                        }
                    } catch (cacheErr) {
                        console.error(
                            "Failed to update localStorage cache:",
                            cacheErr,
                        )
                    }
                }
            } catch (err) {
                console.error(
                    `Failed to refresh community ${communityId}:`,
                    err,
                )
            }
        }, 1000) // Max 1 refresh per second for this community

        loadCommunities()

        // Listen for full community updates (e.g., when persons are enriched with location)
        const handleCommunitiesUpdate = () => {
            // Security Fix #6: Clear cache before full refresh to ensure fresh data
            localStorage.removeItem("chaperon_user_communities")
            loadCommunities()
        }
        window.addEventListener("communitiesUpdated", handleCommunitiesUpdate)

        // Listen for targeted single-community refreshes. Nothing dispatches
        // "communityUpdated" right now (it used to come from the removed
        // WebSocket); kept as the integration point for the planned
        // FCM-for-web service worker / focus-refetch (IMPROVEMENTS.md item 9).
        const handleCommunityUpdate = (event) => {
            const { communityId } = event.detail
            if (communityId) {
                // Fetch fresh data and update cache for this specific community
                debouncedRefreshCommunity(communityId)
            }
        }
        window.addEventListener("communityUpdated", handleCommunityUpdate)

        return () => {
            clearInterval(pollId)
            document.removeEventListener("visibilitychange", refreshIfDue)
            activityEvents.forEach((name) =>
                window.removeEventListener(name, onActivity),
            )
            window.removeEventListener(
                "communitiesUpdated",
                handleCommunitiesUpdate,
            )
            window.removeEventListener(
                "communityUpdated",
                handleCommunityUpdate,
            )
        }
    }, [])

    const handleRefreshCommunities = () => {
        window.dispatchEvent(new Event("communitiesUpdated"))
    }

    const addToast = (message) => {
        const id = Date.now()
        setToasts((prev) => [...prev, { id, message, variant: "dark" }])
        setTimeout(() => {
            setToasts((prev) => prev.filter((toast) => toast.id !== id))
        }, 5000)
    }

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id))
    }

    // Real-time delivery of new-notification toasts and remote community
    // refreshes previously came in over a WebSocket (useWebSocketNotifications),
    // which never actually connected in production -- App Engine Standard
    // doesn't support WebSockets, so every handshake 404'd and the client
    // retried forever. Removed here (IMPROVEMENTS.md item 9, step 1). The
    // "communityUpdated" / "communitiesUpdated" / "notificationCreated"
    // listeners are kept as the integration points for the planned
    // replacements: FCM-for-web for notifications and a visibility-gated
    // poll + focus refetch for community freshness (item 9, steps 2 & 4).
    // Local mutations still dispatch "communitiesUpdated" themselves, so
    // the dashboard already refreshes after actions taken in this tab.

    const addCommunityMember = () => {
        setOpenAddMemberModal(true)
    }

    const closeAddMemberModal = () => {
        setOpenAddMemberModal(false)
    }

    const handleAddMemberSubmit = async (memberData) => {
        console.log("Member data submitted:", memberData)

        const storedUser = JSON.parse(
            localStorage.getItem("chaperon_user_data"),
        )

        if (!storedUser?.id) return

        try {
            let community
            let chaperees = []

            // If creating a new community, call the API first
            if (memberData.newCommunityName && !memberData.communityId) {
                // Create community via API
                const createResponse = await createCommunity({
                    user_id: storedUser.id,
                    name: memberData.newCommunityName,
                    chaperees: "[]", // Empty initially
                })

                if (createResponse.status === "success") {
                    community = createResponse.community
                    addToast(
                        `Community "${community.name}" created successfully!`,
                        "success",
                    )
                } else {
                    addToast(
                        createResponse?.message || "Failed to create community",
                        "error",
                    )
                    return
                }
            } else {
                // Find existing community from current state (not localStorage)
                // Convert communityId to number for comparison (it comes as string from form select)
                const communityIdNum = parseInt(memberData.communityId, 10)
                community = communities.find((c) => c.id === communityIdNum)

                if (!community) {
                    console.error(
                        "Community not found. Looking for ID:",
                        memberData.communityId,
                        "Available communities:",
                        communities.map((c) => ({ id: c.id, name: c.name })),
                    )
                    addToast("Community not found", "error")
                    return
                }
            }

            // Parse existing chaperees
            // Note: fetchCommunityPlaces transforms chaperees into separate member arrays ('places' and 'persons')
            // We need to reconstruct the chaperees array from these
            if (community.places || community.persons) {
                // Community is from fetchCommunityPlaces (transformed structure)
                chaperees = [
                    ...(community.places || []),
                    ...(community.persons || []),
                ]
            } else if (community.chaperees) {
                // Community is from API response (original structure)
                chaperees =
                    typeof community.chaperees === "string"
                        ? JSON.parse(community.chaperees)
                        : community.chaperees || []
            }

            // Check for duplicates
            if (memberData.type === "place") {
                const placeExists = chaperees.some(
                    (c) => c.type === "place" && c.id === memberData.place.id,
                )
                if (placeExists) {
                    addToast("This member is already in the community", "error")
                    return
                }
            } else if (memberData.type === "person") {
                const personExists = chaperees.some(
                    (c) =>
                        c.type === "person" &&
                        (c.username === memberData.identifier ||
                            c.email === memberData.identifier),
                )
                if (personExists) {
                    addToast("This member is already in the community", "error")
                    return
                }

                // Look up the user BEFORE writing anything to the database.
                // This prevents orphaned chaperee records for unresolvable identifiers.
                let recipientUserId = null
                let recipientEmail = null
                const isEmail = memberData.identifier.includes("@")

                try {
                    const lookup = await lookupUserByIdentifier(
                        community.id,
                        memberData.identifier,
                    )

                    if (lookup.notFound) {
                        if (!isEmail) {
                            // Username not found - abort entirely
                            addToast(
                                "No user found with that username. Please check the username, or use an email address to invite someone who hasn't joined yet.",
                                "error",
                            )
                            return
                        }
                        // Email not found in system - valid non-user invite
                        recipientEmail = memberData.identifier
                    } else {
                        // Existing user: sendInvitation resolves their email
                        // from recipient_user_id server-side, so we only need
                        // the id here.
                        recipientUserId = lookup.userId
                    }
                } catch (lookupError) {
                    console.error("Error looking up user:", lookupError)
                    addToast(
                        "Could not verify the user. Please try again.",
                        "error",
                    )
                    return
                }

                // Stash resolved values on memberData for use after chaperee creation
                memberData._recipientUserId = recipientUserId
                memberData._recipientEmail = recipientEmail
            }

            // Create chaperee record first via API
            let chapereeId
            try {
                if (memberData.type === "place") {
                    // Create Place chaperee
                    const chapereeData = {
                        member_id: String(memberData.place.id),
                        type: "place",
                        user_id: storedUser.id,
                        community_id: community.id,
                        slug: memberData.place.slug || null,
                        name:
                            memberData.place.name ||
                            memberData.place.fromGoogle?.name ||
                            null,
                        status: "active",
                        safety: memberData.place.overall_safety || null,
                        safety_overview:
                            memberData.place.overall_safety_overview || null,
                        threshold: memberData.threshold || 60,
                        pinned: 0,
                    }
                    const chapereeResponse = await addChaperee(chapereeData)
                    if (
                        chapereeResponse.status === "success" &&
                        chapereeResponse.chaperee
                    ) {
                        chapereeId = chapereeResponse.chaperee.id
                    } else {
                        throw new Error("Failed to create chaperee record")
                    }
                } else if (memberData.type === "person") {
                    // Create person chaperee
                    const chapereeData = {
                        member_id: memberData.userId || memberData.identifier,
                        type: "person",
                        user_id: storedUser.id,
                        community_id: community.id,
                        username: memberData.identifier,
                        status: memberData.status || "invited",
                        threshold: memberData.threshold || 60,
                        identifier: memberData.identifier,
                        pinned: 0,
                    }
                    const chapereeResponse = await addChaperee(chapereeData)
                    if (
                        chapereeResponse.status === "success" &&
                        chapereeResponse.chaperee
                    ) {
                        chapereeId = chapereeResponse.chaperee.id
                    } else {
                        throw new Error("Failed to create chaperee record")
                    }
                }
            } catch (chapereeError) {
                console.error("Error creating chaperee:", chapereeError)
                addToast(
                    "Failed to create chaperee record. Please try again.",
                    "error",
                )
                return
            }

            // Insert chaperee into community via API
            const insertResponse = await insertChaperee({
                user_id: storedUser.id,
                community_id: community.id,
                chaperee_id: chapereeId,
            })

            if (insertResponse.status === "success") {
                const memberName =
                    memberData.type === "place"
                        ? memberData.place?.fromGoogle?.name ||
                          memberData.place?.name
                        : memberData.identifier
                addToast(`${memberName} added successfully!`, "success")

                // Fire invitation for person members
                if (memberData.type === "person") {
                    const invitePayload = {
                        community_id: community.id,
                        role: memberData.role || "citizen",
                    }
                    if (memberData._recipientUserId) {
                        invitePayload.recipient_user_id =
                            memberData._recipientUserId
                    }
                    if (memberData._recipientEmail) {
                        invitePayload.recipient_email =
                            memberData._recipientEmail
                    }

                    try {
                        await sendInvitation(invitePayload)
                        addToast(
                            `Invitation sent to ${memberData.identifier}`,
                            "success",
                        )
                    } catch (inviteError) {
                        if (inviteError.status === 401) {
                            navigate("/user/login", {
                                state: { from: { pathname: "/community" } },
                            })
                            return
                        } else if (inviteError.status === 409) {
                            addToast(
                                "A pending invitation already exists for this person.",
                                "info",
                            )
                        } else {
                            console.error(
                                "Failed to send invitation:",
                                inviteError,
                            )
                            addToast(
                                "Member added, but the invitation could not be sent. You can resend it later.",
                                "warning",
                            )
                        }
                    }
                }

                // Clear localStorage to force refresh from API
                // This ensures we get the updated community data with proper chaperee IDs from the backend
                localStorage.removeItem("chaperon_user_communities")

                // Refresh communities to get updated data with proper chaperee IDs
                const updatedCommunities = await fetchCommunityPlaces(
                    storedUser.id,
                )
                setCommunities(updatedCommunities)

                // Dispatch event to update sidebar tooltips
                window.dispatchEvent(new Event("communitiesUpdated"))
            } else {
                addToast(
                    insertResponse?.message || "Failed to add member",
                    "error",
                )
            }
        } catch (error) {
            console.error("Error adding member:", error)
            addToast(error?.message || "Failed to add member", "error")
        }
    }

    const handleRemoveMember = async (communityId, memberId, memberType) => {
        try {
            const storedUser = JSON.parse(
                localStorage.getItem("chaperon_user_data"),
            )
            if (!storedUser?.id) {
                console.error("User not logged in")
                addToast("User not logged in")
                return
            }

            // Find the community
            const community = communities.find((c) => c.id === communityId)
            if (!community) {
                console.error("Community not found")
                addToast("Community not found")
                return
            }

            // Find the member being removed to get their name for toast message
            let memberName = `${memberType}`
            const allMembers = [
                ...(community.places || []),
                ...(community.persons || []),
            ]
            const memberToRemove = allMembers.find(
                (chaperee) =>
                    chaperee.type === memberType && chaperee.id === memberId,
            )

            if (memberToRemove) {
                memberName =
                    memberToRemove.name ||
                    memberToRemove.username ||
                    `${memberType}`
            }

            console.log(
                `Removing chaperee ${memberId} from community ${communityId}`,
            )

            // Call deleteChaperees API
            const deleteResponse = await deleteChaperees({
                community_id: communityId,
                user_id: storedUser.id,
                chaperee_ids: [memberId],
            })

            if (deleteResponse.status === "success") {
                // Clear localStorage to force refresh
                localStorage.removeItem("chaperon_user_communities")

                // Refresh communities
                const updatedCommunities = await fetchCommunityPlaces(
                    storedUser.id,
                )
                setCommunities(updatedCommunities)

                // Dispatch event to update sidebar tooltips
                window.dispatchEvent(new Event("communitiesUpdated"))

                // Show success toast
                addToast(`${memberName} removed from ${community.name}`)

                console.log(
                    `${memberType} member removed successfully from community ${communityId}`,
                )
            } else {
                addToast(deleteResponse?.message || "Failed to remove member")
            }
        } catch (error) {
            console.error("Error removing member:", error)
            addToast(error?.message || "Failed to remove member")
        }
    }

    const handleDeleteCommunity = async (communityId) => {
        try {
            const storedUser = JSON.parse(
                localStorage.getItem("chaperon_user_data"),
            )
            if (!storedUser?.id) {
                console.error("User not logged in")
                addToast("User not logged in", "error")
                return
            }

            // Find community name for confirmation message
            const community = communities.find((c) => c.id === communityId)
            const communityName = community?.name || "Community"

            // Delete community via API
            const deleteResponse = await deleteCommunity({
                id: communityId,
                user_id: storedUser.id,
            })

            if (deleteResponse.status === "success") {
                addToast(`${communityName} deleted successfully!`, "success")

                // Clear localStorage to force refresh from API
                localStorage.removeItem("chaperon_user_communities")

                // Refresh communities to get updated data
                const updatedCommunities = await fetchCommunityPlaces(
                    storedUser.id,
                )
                setCommunities(updatedCommunities)

                // Dispatch event to update sidebar tooltips
                window.dispatchEvent(new Event("communitiesUpdated"))
            } else {
                addToast(
                    deleteResponse?.message || "Failed to delete community",
                    "error",
                )
            }
        } catch (error) {
            console.error("Error deleting community:", error)
            addToast(error?.message || "Failed to delete community", "error")
        }
    }

    const editCommunityMember = (member, communityId) => {
        setEditingMember({ ...member, communityId })
        setOpenEditMemberModal(true)
    }

    const closeEditMemberModal = () => {
        setOpenEditMemberModal(false)
        setEditingMember(null)
    }

    const handleEditCommunityMemberSubmit = async (updatedData) => {
        try {
            const storedUser = JSON.parse(
                localStorage.getItem("chaperon_user_data"),
            )
            if (!storedUser?.id) {
                console.error("User not logged in")
                addToast("User not logged in")
                return
            }

            // Find the community and member
            const community = communities.find(
                (c) => c.id === updatedData.communityId,
            )
            if (!community) {
                console.error("Community not found")
                addToast("Community not found")
                return
            }

            const memberType = updatedData.memberType || editingMember?.type
            const members =
                memberType === "person"
                    ? community.persons || []
                    : community.places || []

            const member = members.find((m) => m.id === updatedData.memberId)
            if (!member) {
                const memberLabel = memberType === "person" ? "Person" : "Place"
                console.error(`${memberLabel} not found in community`)
                addToast(`${memberLabel} not found`)
                return
            }

            console.log(
                `Updating threshold for ${memberType || "member"} ${updatedData.memberId} in community ${updatedData.communityId}`,
            )

            // Call updateChaperee API
            const updateResponse = await updateChaperee({
                chaperee_id: updatedData.memberId,
                user_id: storedUser.id,
                community_id: updatedData.communityId,
                updates: {
                    threshold: updatedData.threshold,
                },
            })

            if (updateResponse.status === "success") {
                // Clear localStorage to force refresh
                localStorage.removeItem("chaperon_user_communities")

                // Refresh communities
                const updatedCommunities = await fetchCommunityPlaces(
                    storedUser.id,
                )
                setCommunities(updatedCommunities)

                // Dispatch event to update sidebar tooltips
                window.dispatchEvent(new Event("communitiesUpdated"))

                addToast(
                    `Updated threshold for ${
                        member.full_name ||
                        member.username ||
                        member.name ||
                        "member"
                    } to ${updatedData.threshold}%`,
                )

                console.log("Member threshold updated successfully")
            } else {
                addToast(
                    updateResponse?.message ||
                        "Failed to update member threshold",
                )
            }
        } catch (error) {
            console.error("Error updating member threshold:", error)
            addToast(error?.message || "Failed to update member threshold")
        }
    }

    const editCommunitySettings = (community) => {
        setEditingCommunity(community)
        setOpenCommunitySettingsModal(true)
    }

    const closeCommunitySettingsModal = () => {
        setOpenCommunitySettingsModal(false)
        setEditingCommunity(null)
    }

    const handleCommunitySettingsSubmit = async (updatedData) => {
        try {
            const storedUser = JSON.parse(
                localStorage.getItem("chaperon_user_data"),
            )
            if (!storedUser?.id) {
                console.error("User not logged in")
                addToast("User not logged in")
                return
            }

            // Find the community
            const community = communities.find(
                (c) => c.id === updatedData.communityId,
            )
            if (!community) {
                console.error("Community not found")
                addToast("Community not found")
                return
            }

            console.log(
                `Updating community settings for ${updatedData.communityId}:`,
                updatedData,
            )

            const hasNameChange =
                updatedData.name && updatedData.name !== community.name
            const hasChiefUpdates =
                updatedData.chiefUpdates &&
                Object.keys(updatedData.chiefUpdates).length > 0

            if (!hasNameChange && !hasChiefUpdates) {
                addToast("No changes to save")
                return
            }

            // Send name change and/or chiefUpdates via PATCH endpoint
            const patchUpdates = {
                ...(hasNameChange && { name: updatedData.name }),
                ...(hasChiefUpdates && {
                    chiefUpdates: updatedData.chiefUpdates,
                }),
            }
            console.log("Sending community PATCH payload:", patchUpdates)

            await patchCommunity(
                updatedData.communityId,
                storedUser.id,
                patchUpdates,
            )

            // Clear localStorage to force refresh
            localStorage.removeItem("chaperon_user_communities")

            // Refresh communities
            const updatedCommunities = await fetchCommunityPlaces(storedUser.id)
            setCommunities(updatedCommunities)

            // Dispatch event to update sidebar tooltips
            window.dispatchEvent(new Event("communitiesUpdated"))

            addToast(`Community settings updated successfully!`)

            console.log("Community settings updated successfully")
        } catch (error) {
            console.error("Error updating community settings:", error)
            addToast(error?.message || "Failed to update community settings")
        }
    }

    const reorderChaperees = async (
        communityId,
        draggedItemId,
        targetItemId,
    ) => {
        try {
            const storedUser = JSON.parse(
                localStorage.getItem("chaperon_user_data"),
            )
            if (!storedUser?.id) {
                console.error("User not logged in")
                addToast("User not logged in")
                return
            }

            // Get the full community data from localStorage
            const communitiesData = localStorage.getItem(
                "chaperon_user_communities",
            )
            if (!communitiesData) {
                console.error("No communities data in localStorage")
                addToast("No communities data found")
                return
            }

            const response = JSON.parse(communitiesData)
            const fullCommunity = response.communities.find(
                (c) => c.id === communityId,
            )

            if (!fullCommunity) {
                console.error("Full community data not found")
                addToast("Full community data not found")
                return
            }

            // Parse existing chaperees
            let existingChaperees = []
            if (fullCommunity.chaperees) {
                try {
                    existingChaperees =
                        typeof fullCommunity.chaperees === "string"
                            ? JSON.parse(fullCommunity.chaperees)
                            : fullCommunity.chaperees
                } catch (error) {
                    console.error("Error parsing chaperees:", error)
                    addToast("Error parsing community members")
                    return
                }
            }

            // Find dragged and target chaperees
            const draggedItem = existingChaperees.find(
                (chaperee) => chaperee.id === draggedItemId,
            )
            const targetItem = existingChaperees.find(
                (chaperee) => chaperee.id === targetItemId,
            )

            if (!draggedItem || !targetItem) {
                console.error("Chaperee not found in community")
                return
            }

            // Get all chaperees sorted by their current order
            const chaperees = existingChaperees.sort((a, b) => {
                const orderA =
                    a.order !== undefined && a.order !== null
                        ? a.order
                        : Number.MAX_SAFE_INTEGER
                const orderB =
                    b.order !== undefined && b.order !== null
                        ? b.order
                        : Number.MAX_SAFE_INTEGER
                if (orderA !== orderB) return orderA - orderB
                if (a.updated_at && b.updated_at) {
                    return new Date(a.updated_at) - new Date(b.updated_at)
                }
                return a.id - b.id
            })

            // Find current positions
            const draggedIndex = chaperees.findIndex(
                (p) => p.id === draggedItemId,
            )
            const targetIndex = chaperees.findIndex(
                (p) => p.id === targetItemId,
            )

            // Reorder the array
            const reorderedChaperees = [...chaperees]
            const [removedItem] = reorderedChaperees.splice(draggedIndex, 1)
            reorderedChaperees.splice(targetIndex, 0, removedItem)

            // Assign sequential integer order values to all chaperees
            const updates = reorderedChaperees.map((chaperee, index) => ({
                id: chaperee.id,
                order: index,
            }))

            console.log(
                `Reordering chaperees in community ${communityId}:`,
                updates,
            )

            // Call order endpoint with array of updates
            await updateChapereeOrder({
                community_id: communityId,
                user_id: storedUser.id,
                order_updates: updates,
            })

            // Clear localStorage to force refresh
            localStorage.removeItem("chaperon_user_communities")

            // Refresh communities
            const updatedCommunities = await fetchCommunityPlaces(storedUser.id)
            setCommunities(updatedCommunities)

            // Dispatch event to update sidebar tooltips
            window.dispatchEvent(new Event("communitiesUpdated"))

            console.log("Chaperees reordered successfully")
        } catch (error) {
            console.error("Error reordering chaperees:", error)
            addToast(error.message || "Failed to reorder items")
        }
    }

    const togglePinMember = async (
        memberId,
        communityId,
        memberType = "place",
    ) => {
        try {
            const storedUser = JSON.parse(
                localStorage.getItem("chaperon_user_data"),
            )
            if (!storedUser?.id) {
                console.error("User not logged in")
                addToast("User not logged in")
                return
            }

            // Find the community and member to get the current pinned state and name
            const community = communities.find((c) => c.id === communityId)
            if (!community) {
                console.error("Community not found")
                addToast("Community not found")
                return
            }

            const members =
                memberType === "person"
                    ? community.persons || []
                    : community.places || []
            const member = members.find((m) => m.id === memberId)

            if (!member) {
                const memberLabel = memberType === "person" ? "Person" : "Place"
                console.error(`${memberLabel} not found in community`)
                addToast(`${memberLabel} not found`)
                return
            }

            const currentPinned = member.pinned || false
            const newPinned = !currentPinned

            console.log(
                `Toggling pin for ${memberType} ${memberId} in community ${communityId} to ${newPinned}`,
            )

            // Call updateChaperee API
            const updateResponse = await updateChaperee({
                chaperee_id: memberId,
                user_id: storedUser.id,
                community_id: communityId,
                updates: {
                    pinned: newPinned,
                },
            })

            if (updateResponse.status === "success") {
                // Clear localStorage to force refresh
                localStorage.removeItem("chaperon_user_communities")

                // Refresh communities
                const updatedCommunities = await fetchCommunityPlaces(
                    storedUser.id,
                )
                setCommunities(updatedCommunities)

                // Dispatch event to update sidebar tooltips
                window.dispatchEvent(new Event("communitiesUpdated"))

                const action = newPinned ? "pinned" : "unpinned"
                addToast(
                    `${
                        member.full_name ||
                        member.username ||
                        member.name ||
                        "Member"
                    } ${action}`,
                )

                console.log("Member pin toggled successfully")
            } else {
                addToast(
                    updateResponse?.message || "Failed to toggle member pin",
                )
            }
        } catch (error) {
            console.error("Error toggling member pin:", error)
            addToast(error?.message || "Failed to toggle member pin")
        }
    }

    return {
        communities,
        isLoadingCommunities,
        isSidebarOpen,
        setIsSidebarOpen,
        toasts,
        addToast,
        removeToast,
        handleRefreshCommunities,
        openAddMemberModal,
        addCommunityMember,
        closeAddMemberModal,
        handleAddMemberSubmit,
        handleRemoveMember,
        handleDeleteCommunity,
        openEditMemberModal,
        editingMember,
        editCommunityMember,
        closeEditMemberModal,
        handleEditCommunityMemberSubmit,
        openCommunitySettingsModal,
        editingCommunity,
        editCommunitySettings,
        closeCommunitySettingsModal,
        handleCommunitySettingsSubmit,
        reorderChaperees,
        togglePinMember,
    }
}
